import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { PendingPassoutListColumns } from '../../appconfig/DatatableSetting';
import { defaultRowOptions, statusFilterOptions, defaultPageRowOptions, paginatorLinks, showingEntries, Skeletonitems, SkeletonbodyTemplate, filterDegreeList, PassoutFilterOptions } from '../../appconfig/Settings';
import { getAllDetails, postAllData } from '../../service/ProductService';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { PASSOUT_APPROVE_STATUS, PENDING_PASSOUT_LIST } from '../../config/ApiConstant';
import { CollegeManageTab } from './CollegeManageTab';
import { Badge } from 'primereact/badge';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { reasonValidate } from '../../config/Validate';

export const PendingPassoutList = () => {
    document.title = 'Pending Passout List | GSPC';

    const toast = useRef<Toast>(null);

    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<string | null>(null);

    const [loading, setLoading] = useState(false);
    const [pendingPassoutList, setPendingPassoutList] = useState<any[]>([]);
    const [statusValue, setStatusValue] = useState<any>(null);
    const [degreeValue, setDegreeValue] = useState<any>(null);

    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [approvalData, setApprovalData] = useState<any>({});
    const [errors, setErrors] = useState<any>({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [passoutStatusValue, setPassoutStatusValue] = useState<any>(null);
    const [statusDialogVisible, setStatusDialogVisible] = useState(false);

    // ufeeffect
    useEffect(() => {
        getPassoutList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // get api passout list api
    const getPassoutList = async () => {
        try {
            setLoading(true);
            const response = await getAllDetails(PENDING_PASSOUT_LIST);
            setPendingPassoutList(response || []);
        } catch (err: any) {
            setPendingPassoutList([]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message });
        } finally {
            setLoading(false);
        }
    };

    // submit status change api call
    const submitStatusChange = async () => {
        const { errors: vErrors, isError } = reasonValidate(approvalData);
        setErrors(vErrors);
        if (isError) return;

        try {
            setSubmitLoading(true);
            const payload: any = {
                id: selectedRow.id,
                status: approvalData.status
            };
            if (approvalData.status === 'Reupload') {
                payload.reason = approvalData.reason;
            }

            const response = await postAllData(PASSOUT_APPROVE_STATUS, payload);
            toast.current?.show({ severity: 'success', summary: 'Success', detail: response?.message });
            hideStatusDialog();
            getPassoutList();
        } catch (err: any) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message });
        } finally {
            setSubmitLoading(false);
        }
    };

    // clear all
    const clearAll = () => {
        setGlobalFieldFilter(null);
        setStatusValue(null);
        setGlobalFilter(null);
    };

    // left toolbar
    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    // right toolbar
    const rightToolbarTemplate = () => <Button icon="pi pi-filter-slash" label="Clear" onClick={clearAll} className="p-button-outlined p-button-primary mr-2" />;

    // Template for actions
    const actionsTemplate = (rowData: any) => {
        return (
            <div className="actions action-btn flex gap-2">
                {/* Open File (only if passout_list exists) */}
                {rowData.passout_list && <Button icon="pi pi-file" className="p-button-primary" onClick={() => window.open(rowData.passout_list, '_blank')} tooltip="Open File" tooltipOptions={{ position: 'top' }} />}
            </div>
        );
    };

    // status template
    const passoutStatusTemplate = (row: any) => {
        const rawValue = row.passout_status ?? '';
        const lower = rawValue.toLowerCase();

        let severity: 'success' | 'warning' | 'danger' | 'info' = 'info';
        if (lower.includes('regular')) {
            severity = 'success';
        } else if (lower.includes('remedial')) {
            severity = 'warning';
        }

        return <Badge value={rawValue || 'N/A'} severity={severity} />;
    };

    // passout status filter template
    const passoutFilterTemplate = (options: any) => {
        return (
            <Dropdown
                filter
                value={passoutStatusValue}
                options={PassoutFilterOptions}
                onChange={(e) => {
                    setPassoutStatusValue(e.value);
                    options.filterCallback(e.value.code, options.index);
                }}
                placeholder="Filter by Passout status"
                className="p-column-filter"
                optionLabel="name"
                showClear
            />
        );
    };

    // status template badge & switch
    const statusTemplate = (row: any) => {
        const statusMap: any = {
            0: { label: 'Pending', severity: 'warning' },
            1: { label: 'Approved', severity: 'success' },
            2: { label: 'Reupload', severity: 'primary' },
            3: { label: 'Reuploaded', severity: 'info' }
        };
        const s = statusMap[row.status] || { label: 'Unknown', severity: 'secondary' };
        return <Badge value={s.label} severity={s.severity} />;
    };

    // status filter dropdwon
    const statusFilterTemplate = (options: any) => (
        <Dropdown
            filter
            value={statusValue}
            options={statusFilterOptions}
            onChange={(e) => {
                setStatusValue(e.value);
                options.filterCallback(e.value.code, options.index);
            }}
            placeholder="Filter by status"
            className="p-column-filter"
            optionLabel="name"
            showClear
        />
    );

    //Change Status dialog open
    const openStatusDialog = (row: any) => {
        setSelectedRow(row);
        setApprovalData({});
        setErrors({});
        setStatusDialogVisible(true);
    };
    //Change Status dialog close
    const hideStatusDialog = () => {
        setStatusDialogVisible(false);
        setApprovalData({});
        setErrors({});
    };

    // filter by degree dropdown
    const filterByDegree = (options: any) => (
        <Dropdown
            filter
            value={degreeValue}
            options={filterDegreeList}
            onChange={(e) => {
                setDegreeValue(e.value);
                options.filterCallback(e.value ? e.value.name : null, options.index);
            }}
            placeholder="Filter by degree"
            className="p-column-filter"
            optionLabel="name"
            showClear
        />
    );

    // change status template
    const changeStatusTemplate = (rowData: any) => {
        return (
            <div className="actions action-btn flex gap-2">
                <Button icon="pi pi-pencil" disabled={rowData.status === 1 || rowData.status === 2} className="p-button-primary" tooltip="Change Status" tooltipOptions={{ position: 'top' }} onClick={() => openStatusDialog(rowData)} />
            </div>
        );
    };

    // on input change
    const onInputChange = (e: any, name: string) => {
        const val = e.target ? e.target.value : e.value;
        setApprovalData({ ...approvalData, [name]: val });
        setErrors({ ...errors, [name]: '' });
    };

    // reason template
    const reasonTemplate = (rowData: any) => {
        return rowData.status === 2 || rowData.status === 3 ? rowData.reason || '-' : '-';
    };

    // Template for college name
    const collegeNameTemplate = (rowData: any, rowIndex: any) => {
        return (
            <>
                <div>
                    <Button tooltip={rowData?.college_name} className="p-button-custom-tooltip cl-manage" tooltipOptions={{ position: 'top' }}>
                        <div className="college-name-div">{rowData?.college_name}</div>
                    </Button>
                </div>
            </>
        );
    };

    return (
        <>
            <CollegeManageTab mainTabLabel="Passout List" />
            <Toast ref={toast} />
            <AppBreadcrumb currentMenu="Passout List" pageName="Pending Passout List" pageTitle="Pending Passout List" goToPage />

            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate} />

                        {!loading ? (
                            <DataTable
                                value={pendingPassoutList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={pendingPassoutList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                paginatorTemplate={paginatorLinks}
                                currentPageReportTemplate={showingEntries}
                                emptyMessage="No Data available"
                                removableSort
                            >
                                {PendingPassoutListColumns.map((col) => {
                                    if (col.field === 'sr_no') return <Column key={col.field} header={col.header} body={(_: any, { rowIndex }) => rowIndex + 1} />;
                                    else if (col.field === 'college_name') {
                                        return <Column key={col.field} field={col.field} header={col.header} body={collegeNameTemplate} sortable filter filterPlaceholder={`Search by ${col.header}`} />;
                                    } else if (['from_year', 'to_year'].includes(col.field)) return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={`Search by ${col.header}`} sortable />;
                                    else if (col.field === 'reason') {
                                        return <Column key={col.field} header={col.header} body={reasonTemplate} />;
                                    } else if (col.field === 'degree_name') return <Column key={col.field} field={col.field} header={col.header} filter filterElement={filterByDegree} />;
                                    else if (col.field === 'change_status') {
                                        return <Column key={col.field} header={col.header} body={changeStatusTemplate} />;
                                    } else if (col.field === 'passout_status') {
                                        return <Column key={col.field} field={col.field} header={col.header} filter filterElement={passoutFilterTemplate} body={passoutStatusTemplate} />;
                                    } else if (col.field === 'action') {
                                        return <Column key={col.field} header={col.header} body={actionsTemplate} />;
                                    } else if (col.field === 'status') return <Column key={col.field} field={col.field} header={col.header} body={statusTemplate} filter filterElement={statusFilterTemplate} />;
                                    else return <Column key={col.field} field={col.field} header={col.header} sortable />;
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {PendingPassoutListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            {/* New Re-upload Dialog */}
            <Dialog
                header="Change Status"
                visible={statusDialogVisible}
                draggable={false}
                style={{ width: '40vw', maxWidth: '500px' }}
                onHide={hideStatusDialog}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary" onClick={hideStatusDialog} />
                        <Button label="Submit" icon="pi pi-check" className="p-button-success" loading={submitLoading} onClick={submitStatusChange} />
                    </div>
                }
            >
                <div className="field p-fluid mb-3">
                    <label>Status *</label>
                    <Dropdown
                        value={approvalData.status || ''}
                        options={[
                            { label: 'Approved', value: 'Approve' },
                            { label: 'Reupload', value: 'Reupload' }
                        ]}
                        onChange={(e) => onInputChange(e, 'status')}
                        placeholder="Select Status"
                        className={errors.status && 'p-invalid'}
                    />
                    <small className="p-invalid-txt">{errors['status']}</small>
                </div>

                {approvalData.status === 'Reupload' && (
                    <div className="field p-fluid">
                        <label>Reason *</label>
                        <InputTextarea value={approvalData.reason || ''} rows={5} autoResize onChange={(e) => onInputChange(e, 'reason')} className={errors.reason && 'p-invalid'} placeholder="Enter reason" />
                        <small className="p-invalid-txt">{errors['reason']}</small>
                    </div>
                )}
            </Dialog>
        </>
    );
};
